"use client";

import { useState, useRef, useEffect } from "react";
import styles from "./Select.module.css";

export interface Option {
    value: string;
    label: string;
}

export interface SelectProps {
    id?: string;
    label: string;
    value: string;
    options: Option[];
    onChange: (value: string) => void;
    className?: string;
}

export function Select({ id, label, value, options, onChange, className = "" }: SelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const selectId = id || label.replace(/\s+/g, "-").toLowerCase();

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (optionValue: string) => {
        onChange(optionValue);
        setIsOpen(false);
    };

    const selectedOption = options.find((opt) => opt.value === value);

    return (
        <div
            className={`${styles.customSelectWrapper} ${isOpen ? styles.active : ""} ${value ? styles.hasValue : ""} ${className}`}
            ref={wrapperRef}
            onClick={() => setIsOpen(!isOpen)}
        >
            <div className={styles.selectTrigger} tabIndex={0} id={selectId}>
                <span className={styles.selectedValue}>{selectedOption ? selectedOption.label : ""}</span>
                <span className={styles.label}>{label}</span>
                <div className={styles.selectIcon}></div>
            </div>

            <div className={styles.optionsList}>
                {options.map((option) => (
                    <div
                        key={option.value}
                        className={`${styles.optionItem} ${option.value === value ? styles.selected : ""}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleSelect(option.value);
                        }}
                    >
                        {option.label}
                    </div>
                ))}
            </div>
        </div>
    );
}
